import type { User } from '~/types'

export const useAuth = () => {
  const user = useState<User | null>('auth:user', () => null)
  const initialized = useState<boolean>('auth:initialized', () => false)
  const sessionExpiredMessage = useState<string | null>('auth:sessionExpired', () => null)

  async function checkAuth() {
    if (initialized.value) return

    try {
      const data = await useApi<{ user: User }>('/auth/me')
      user.value = data.user
    } catch (err: any) {
      user.value = null
      // If session was terminated (logged in from another device)
      if (err.data?.code === 'SESSION_EXPIRED') {
        sessionExpiredMessage.value = 'Сессия завершена. Вы вошли с другого устройства.'
      } else if (err.data?.code === 'SESSION_IP_CHANGED') {
        sessionExpiredMessage.value = 'Ваш IP-адрес изменился, поэтому сессия завершена. Войдите заново.'
      }
    } finally {
      initialized.value = true
    }
  }

  // При включённой 2FA сервер не выдаёт сессию, а присылает код в Telegram и challenge —
  // тогда вход завершает verifyLoginCode
  async function login(email: string, password: string, captchaToken?: string): Promise<{ requires2fa: true; challenge: string } | { requires2fa: false }> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const data = await useApi<{ user?: User; requires_2fa?: boolean; challenge?: string }>('/auth/login', {
      method: 'POST',
      body: { email, password, timezone, captchaToken }
    })
    if (data.requires_2fa && data.challenge) return { requires2fa: true, challenge: data.challenge }
    user.value = data.user!
    initialized.value = true
    return { requires2fa: false }
  }

  async function verifyLoginCode(challenge: string, code: string) {
    const data = await useApi<{ user: User }>('/auth/login/2fa', {
      method: 'POST',
      body: { challenge, code }
    })
    user.value = data.user
    initialized.value = true
  }

  async function logout() {
    await useApi('/auth/logout', {
      method: 'POST'
    })
    user.value = null
    initialized.value = false
    navigateTo('/login')
  }

  async function updateProfile(data: Partial<User>) {
    const response = await useApi<{ user: User }>('/auth/profile', {
      method: 'PUT',
      body: data
    })
    // PUT /profile отдаёт урезанного пользователя (без team/features) — сливаем,
    // иначе после сохранения профиля пропадали команда, фичи и рамка тарифа.
    user.value = { ...user.value, ...response.user } as User
    return response
  }

  async function updatePassword(currentPassword: string, newPassword: string) {
    await useApi('/auth/change-password', {
      method: 'POST',
      body: {
        current_password: currentPassword,
        new_password: newPassword
      }
    })
  }

  // Permission helpers
  // User IDs: Вадим=1, Ярослав=8
  const VADIM_ID = 1
  const YAROSLAV_ID = 8

  // Суперадмин CRM (управляет командами и фиче-флагами) или админ своей команды —
  // team_role пришёл из team_members.team_role_id (см. GET /api/auth/me).
  const isSuperAdmin = computed(() => !!user.value?.is_super_admin)
  const isTeamAdmin = computed(() => user.value?.team_role === 'admin')
  const isAdmin = computed(() => isSuperAdmin.value || isTeamAdmin.value)
  const isManager = computed(() => user.value?.team_role === 'manager')
  // Тариф «Команда» (teams.plan_type = 'team') — у аватарок цветная рамка (UserAvatar plan-ring).
  const hasTeamPlan = computed(() => user.value?.team?.plan_type === 'team' && user.value.team.plan_enabled !== false)
  const isAdminOrManager = computed(() => isAdmin.value || isManager.value)

  // Права роли: админу команды и суперадмину можно всё, остальным — как настроил админ
  // команды (GET /api/auth/me → permissions)
  function can(permission: string): boolean {
    if (isAdmin.value) return true
    return user.value?.permissions?.[permission] ?? false
  }

  const canChangeStatus = computed(() => can('tenders.change_status'))

  // Only Вадим (admin)
  const isVadim = computed(() => user.value?.id === VADIM_ID)

  // Автоподбор по каталогу — управляется суперадмином на уровне команды
  // (team_feature_flags['feature:auto_search']), см. GET /api/auth/me → features.
  // Общий каталог товаров (разделы «Каталог», конструктор ПК, поиск по каталогу в тендере) —
  // только пользователям из platform_settings.catalog_readers (GET /api/auth/me → catalog_access)
  const canViewCatalog = computed(() => user.value?.catalog_access !== false)

  const canSearchCatalog = computed(() => {
    if (!canViewCatalog.value) return false
    if (isSuperAdmin.value) return true
    return user.value?.features?.['feature:auto_search'] ?? false
  })

  // Доступ к AI-ассистенту — тоже флаг команды
  const canUseAiAssistant = computed(() => {
    if (isSuperAdmin.value) return true
    return user.value?.features?.['feature:ai_assistant'] ?? false
  })

  const canChangePriority = computed(() => can('tenders.change_priority'))

  function hasFeature(featureKey: string): boolean {
    if (isSuperAdmin.value) return true
    return user.value?.features?.[featureKey] ?? true
  }

  return {
    user: readonly(user),
    initialized: readonly(initialized),
    sessionExpiredMessage: readonly(sessionExpiredMessage),
    isAdmin,
    isManager,
    isAdminOrManager,
    hasTeamPlan,
    isSuperAdmin,
    isTeamAdmin,
    can,
    verifyLoginCode,
    canChangeStatus,
    canChangePriority,
    canSearchCatalog,
    canViewCatalog,
    canUseAiAssistant,
    hasFeature,
    isVadim,
    login,
    logout,
    checkAuth,
    updateProfile,
    updatePassword
  }
}
